/* tslint:disable:no-multiple-render-methods */
/**
 * Typeahead is a special case, due to the fact that it is a UITK component,
 * and there are many clients consuming it.
 */

import * as React from 'react';
import { inject, observer } from 'mobx-react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ContextStore, QueryRoute, RouteStore } from 'bernie-core';
import { ErrorBoundary } from 'bernie-view';
import { Layer } from 'bernie-view';
import { LocalizedText } from 'bernie-l10n';

import { FilterHeaderLevel, FilterHeaderSize, FilterHeaderWeight } from 'constants/filter-header-config';

import { classNames } from 'uitk-react-utils';
import { ESSAdapter, TypeaheadToolbarVersion, UitkTypeahead } from 'uitk-react-typeahead';
import { UitkHeading, UitkText } from 'uitk-react-text';

import { FormFieldBinding } from './form-field-binding';
import { SearchFormFieldProps } from 'components/common/search-form';

export interface HotelNameProps extends SearchFormFieldProps {
  context?: ContextStore;
  routeStore?: RouteStore;
  regionId?: string;
  analytics?: any;
}

@FormFieldBinding
@inject('context', 'routeStore', 'analytics')
@observer
export class HotelName extends React.Component<HotelNameProps> {
  private inputRef;
  public constructor(props) {
    super(props);
    this.inputRef = React.createRef();

    this.dismissTypeahead = this.dismissTypeahead.bind(this);
    this.openTypeahead = this.openTypeahead.bind(this);
    this.updateTypeahead = this.updateTypeahead.bind(this);
    this.getHotelNameFilterDialog = this.getHotelNameFilterDialog.bind(this);
    this.sendTracking = this.sendTracking.bind(this);
    this.handleClear = this.handleClear.bind(this);
  }

  private handleClear() {
    const { updateModel } = this.props;

    if (typeof updateModel === 'function') {
      updateModel({
        target: {
          name: 'hotelName',
          value: ''
        }
      });
    }
  }

  public render() {
    const { context, model, modelKey, sortAndFilterOptions, showSeparator } = this.props;
    const value = (model && model[modelKey]) || '';
    const regionId = model && model.regionId;
    const hotelName = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const label = hotelName && hotelName.label;
    const placeholder = hotelName && hotelName.placeholder;
    // tslint:disable-next-line:max-line-length
    const legendClasses = classNames('uitk-cell filter-title', {
      'all-t-padding-six uitk-card-separator-top ': showSeparator
    });

    if (!hotelName) {
      return null;
    }

    const defaultEssAdapterConfig = {
      client: 'Hotels.Search',
      dest: true,
      features: 'ta_hierarchy|postal_code|contextual_ta',
      guid: context && context.deviceId,
      inputId: 'hotel-name-filter',
      lob: 'HOTELS',
      locale: context && context.locale,
      maxResults: 10,
      regionId: parseInt(regionId, 10),
      regionType: 128,
      siteId: context && context.site && context.site.id
    };

    const defaultTypeaheadProps = {
      allowExactMatch: true,
      icon: 'search',
      name: 'hotelName',
      label: placeholder,
      handleClear: this.handleClear,
      hasClearButton: true,
      onClick: this.openTypeahead,
      onDismiss: this.dismissTypeahead,
      onUpdate: this.updateTypeahead,
      renderDialog: this.getHotelNameFilterDialog,
      value,
      toolbarVersion: TypeaheadToolbarVersion.V1,
      typeaheadPlaceholder: placeholder,
      ref: this.inputRef
    };

    return (
      <React.Fragment>
        <fieldset className="uitk-card-separator-bottom all-b-padding-six all-b-margin-six all-t-margin-six">
          <legend className={legendClasses}>
            <UitkText size={FilterHeaderSize.HEADING_500} weight={FilterHeaderWeight.BOLD}>
              {label}
            </UitkText>
          </legend>
          <ErrorBoundary label="hotel-name.typeahead">
            <AbacusExperiment name="ESS_Two_Line_Display">
              <AbacusControl>
                <UitkTypeahead {...defaultTypeaheadProps} adapter={new ESSAdapter(defaultEssAdapterConfig)} />
              </AbacusControl>
              <AbacusVariant bucket={1}>
                <UitkTypeahead
                  {...defaultTypeaheadProps}
                  adapter={
                    new ESSAdapter({
                      ...defaultEssAdapterConfig,
                      features: 'ta_hierarchy|postal_code|contextual_ta|consistent_display'
                    })
                  }
                />
              </AbacusVariant>
              <AbacusVariant bucket={2}>
                <UitkTypeahead
                  {...defaultTypeaheadProps}
                  adapter={
                    new ESSAdapter({
                      ...defaultEssAdapterConfig,
                      features: 'ta_hierarchy|postal_code|contextual_ta|consistent_display'
                    })
                  }
                />
              </AbacusVariant>
            </AbacusExperiment>
          </ErrorBoundary>
        </fieldset>
        <UitkHeading level={FilterHeaderLevel.LEVEL_1} size={FilterHeaderSize.HEADING_500}>
          <LocalizedText message="hotels.searchResults.filtersDialog.hotelName.label" />
        </UitkHeading>
      </React.Fragment>
    );
  }

  private dismissTypeahead() {
    this.props.routeStore.goBack();
    if (this.inputRef && this.inputRef.current && typeof this.inputRef.current.focusTypeaheadInput === 'function') {
      this.inputRef.current.focusTypeaheadInput();
    }
  }

  private openTypeahead() {
    const { routeStore } = this.props;

    if (routeStore && typeof routeStore.updateSearch === 'function') {
      routeStore.updateSearch({ pwaDialogNested: 'hotel-name-typeahead' });
    }
  }

  private updateTypeahead(selection) {
    const term = selection.term;

    this.props.updateModel({
      target: {
        name: 'hotelName',
        value: term
      }
    });
    this.sendTracking(term);
  }

  private sendTracking(term) {
    this.props.analytics.clickTracker(`.hotelName.${term}`, `hotelName.${term}`)();
  }

  private getHotelNameFilterDialog(children) {
    return (
      <QueryRoute query={{ pwaDialogNested: 'hotel-name-typeahead' }}>
        <Layer id="hotel-name-typeahead" onDismiss={this.dismissTypeahead}>
          {children}
        </Layer>
      </QueryRoute>
    );
  }
}
